import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronRight } from 'lucide-react';

export default function PrivacyPolicy() {
  const lastUpdated = "April 7, 2026";

  const privacyContent = [
    {
      id: "1",
      title: "1. Consent to Privacy Policy",
      content: "By your continued use of the merchant's services following the posting of this Privacy Policy, you agree to this Privacy Policy."
    },
    {
      id: "2",
      title: "2. Information Collection and Personally Identifiable Information",
      content: "We collect the following personal data:",
      list: [
        "Registration data: email address, password, first name, last name, phone number, and payment details.",
        "Usage data: information about your activity when using our services.",
        "Marketing data: email address for communication of offers, newsletters, or updates."
      ],
      extraText: "We use this information to create and manage your account, contact you, enable payments, prevent fraud, customize services, and improve your experience.",
      boxes: [
        {
          heading: "Legal basis:",
          items: [
            "Contract performance (to provide services).",
            "Legitimate interests (direct marketing and service improvement).",
            "Compliance with legal obligations (e.g., accounting, tax)."
          ]
        },
        {
          heading: "Your rights:",
          text: "You may request correction, deletion, access, data portability, restriction of processing, or object to processing (especially marketing). We will honor such requests unless an overriding legal obligation applies. We entrust processing to GDPR-compliant data processors, such as hosting providers and payment processors. We may also be legally required to disclose information to authorities (e.g., police or courts)."
        }
      ]
    },
    {
      id: "3",
      title: "3. Information Use",
      content: "We use your personal data for:",
      list: [
        "Processing payments securely.",
        "Customizing services, ads, and recommendations.",
        "Contacting you and responding to requests.",
        "Conducting research and improving services.",
        "Preventing fraud and ensuring security."
      ]
    },
    {
      id: "4",
      title: "4. Information Sharing and Disclosure",
      content: "We will not disclose your personally identifiable information except:",
      list: [
        "Where required by law.",
        "For service provision (e.g., hosting, payment processing).",
        "In connection with corporate transactions (e.g., merger or sale).",
        "With carefully selected contractors bound by confidentiality."
      ],
      extraText: "We may share aggregate, anonymous information with partners, advertisers, or content distributors."
    },
    {
      id: "5",
      title: "5. Changing or Deleting Your Information",
      content: "You may review, update, correct, or delete your personal information by contacting us. Deletion may deactivate your account. We may retain archived data where legally required."
    },
    {
      id: "6",
      title: "6. Use of Cookies and Similar Technologies",
      content: "The merchant uses cookies, web beacons, device fingerprinting, and similar technologies for functionality, analytics, personalization, and advertising.",
      gridHeading: "Types of cookies used include:",
      grid: [
        "Persistent and session cookies",
        "Strictly necessary cookies",
        "Performance cookies",
        "Functionality cookies",
        "Targeting/advertising cookies"
      ],
      extraText: "Cookies help improve navigation, remember preferences, and display relevant ads. You can manage cookies in your browser settings. Blocking cookies may affect site functionality. We also use Google Analytics and may link cookies to advertising platforms such as Facebook."
    },
    {
      id: "7",
      title: "7. Behavioral Advertising and Online Privacy",
      content: "We may use cookies and online identifiers for behavioral advertising, in line with industry standards. You can manage cookies via browser settings, but some site functions may not work if cookies are disabled.",
      link: {
        text: "youronlinechoices.eu",
        url: "https://youronlinechoices.eu"
      }
    },
    {
      id: "8",
      title: "8. Changes and Updates",
      content: "We may periodically update this Privacy Policy and will notify you by posting the modified version on the Website or contacting you by email."
    },
    {
      id: "9",
      title: "9. Contact",
      content: "Questions, comments, and requests regarding this Privacy Policy are welcomed and should be addressed to the merchant's support team."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A]  relative ">

      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1612550761236-e813928f7271?auto=format&fit=crop&q=80&w=2400"
          alt="Background"
          className="w-full h-full object-cover opacity-[0.03] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />

        {/* Soft Theme Glows */}
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      {/* HEADER */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 relative z-10 pt-40 pb-16 md:pb-24 md:pt-40">

        {/* The Glassmorphism Document Card */}
        <div className="w-full bg-[#1E293B]/80 backdrop-blur-2xl rounded-[2.5rem] border border-slate-700/80 shadow-[0_0_50px_rgba(0,0,0,0.3)] overflow-hidden relative">

          {/* Top highlight bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2563EB] via-purple-500 to-[#F43F5E] opacity-80" />

          {/* Document Header */}
          <div className="px-8 pt-16 pb-8 md:px-16 md:pt-20 md:pb-12 border-b border-slate-700/80">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
              Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Policy</span>
            </h1>
            <p className="text-slate-400 font-medium tracking-wide">
              Last Updated: <span className="text-[#2563EB]">March 2026</span>
            </p>
          </div>

          {/* Document Body */}
          <div className="px-8 py-12 md:px-16 md:py-16 space-y-16">

            {/* Dynamic Rendering of Sections */}
            {privacyContent.map((section) => (
              <section key={section.id} className="space-y-6 scroll-mt-24" id={`section-${section.id}`}>

                {/* Heading */}
                <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">
                    {section.title.split('.')[0]}.
                  </span>
                  {section.title.substring(section.title.indexOf('.') + 1).trim()}
                </h2>

                {/* Main Content Text */}
                <p className="text-slate-300 leading-relaxed text-lg">
                  {section.content}
                </p>

                {/* Bulleted Lists */}
                {section.list && (
                  <ul className="space-y-3 mt-4 pl-2">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-300 leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0 mt-2.5 shadow-[0_0_8px_rgba(37,99,235,0.8)]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Grid Elements (Like the Cookies section) */}
                {section.grid && (
                  <div className="mt-6 space-y-4">
                    <p className="text-slate-300 font-bold">{section.gridHeading}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {section.grid.map((item, i) => (
                        <div key={i} className="bg-[#0F172A]/50 border border-slate-700/80 rounded-xl px-5 py-4 flex items-center gap-3 text-slate-300 transition-colors hover:bg-white/5">
                          <ChevronRight className="w-4 h-4 text-[#2563EB]" />
                          <span className="text-sm font-semibold">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Callout Boxes (Like Legal Basis and Rights) */}
                {section.boxes && (
                  <div className="space-y-4 mt-6">
                    {section.boxes.map((box, i) => (
                      <div key={i} className="bg-[#0F172A]/50 border border-slate-700/80 rounded-2xl p-6 md:p-8">
                        <h3 className="text-white font-bold mb-3">{box.heading}</h3>
                        {box.text && <p className="text-slate-400 leading-relaxed text-sm">{box.text}</p>}
                        {box.items && (
                          <ul className="space-y-2">
                            {box.items.map((item, j) => (
                              <li key={j} className="flex items-start gap-2.5 text-slate-400 text-sm">
                                <div className="w-1 h-1 rounded-full bg-[#F43F5E] shrink-0 mt-2" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Extra Paragraph Text */}
                {section.extraText && (
                  <p className="text-slate-300 leading-relaxed text-lg pt-2">
                    {section.extraText}
                  </p>
                )}

                {/* Custom Links (Contact email) */}
                {section.link && (
                  <p className="pt-2">
                    <a href={section.link.url} className="text-[#2563EB] font-bold hover:text-[#F43F5E] transition-colors hover:underline underline-offset-4">
                      {section.link.text}
                    </a>
                  </p>
                )}

              </section>
            ))}

          </div>

          {/* Document Footer / Support Box */}
          
        </div>

      </main>

      {/* FOOTER */}
      <div className="relative mt-auto">
        <Footer />
      </div>
    </div>
  );
}
